import { usePage } from "@inertiajs/react"

/**
 * OKS-1041 — the fixed-viewport (chat) chain, extracted so the console shells
 * cannot drift apart again.
 *
 * # What this is
 *
 * `Crew/Messages/Index` is a FIXED-VIEWPORT surface: the thread history
 * scrolls INTERNALLY while the shell header and the composer stay put. Every
 * other console page is a normal body-scroll document.
 *
 * `app-layout.tsx` wraps the Messages routes in `flex h-[100dvh] flex-col
 * overflow-hidden` for EVERY role (`isMessagesViewport`, OKS-723/820). That
 * wrapper only works if each layer below it passes the height down — which is
 * what the classes here do.
 *
 * # Why it exists (the OKS-1041 defect)
 *
 * OKS-820 applied this chain to `BrandShell` only. `AgencyShell` never got it,
 * so for an assigned agency member the sidebar wrapper kept `min-h-svh` inside
 * a `100dvh − banner` column and the surplus was CLIPPED by `overflow-hidden`
 * — the composer rendered below the fold and could not be scrolled to, because
 * the wrapper that clipped it is also the one that forbids page scroll.
 *
 * The overflow is `B + max(0, wrapperContent − 100svh)` where `B` is the
 * `shrink-0` banner stack. Note what that means: with NO banner and a SHORT
 * thread the overflow is zero and there is no defect at all. Any test aimed at
 * this bug must therefore use a thread taller than the viewport — the one case
 * that fails under either banner state. A short-thread arm is load-bearing
 * only while a banner happens to be up, and nothing in such an arm would say
 * so. See `BRIEF-OKS-1041.md` §6.9.
 *
 * # Why a shared module rather than a copy in each shell
 *
 * Two shells drifted apart once already and the divergence was invisible for
 * three tickets, partly because `BrandShell`'s own docstring claimed it served
 * the agency role. One definition, two consumers.
 *
 * @see BrandShell
 * @see AgencyShell
 * @see app-layout.tsx — the `h-[100dvh] overflow-hidden` wrapper this pairs with
 */

/**
 * Inertia page components that opt into the fixed-viewport chain.
 *
 * Deliberately a closed set rather than a path prefix: the chain REPLACES
 * body-scroll, so a surface joins it by explicit decision, never by matching a
 * pattern. `Creator/Messages/Index` is absent on purpose — the creator surface
 * is a flex-column shell (OKS-723) with a different chain, not this one.
 */
const FIXED_VIEWPORT_COMPONENTS: readonly string[] = ["Crew/Messages/Index"]

/**
 * The classes each layer of the console chain needs on a fixed-viewport route.
 *
 * ⚠️ `shell` MUST reach `SidebarProvider`'s wrapper through `<AppShell
 * className>`, which merges it into the wrapper's own `cn()` call. The wrapper
 * ships `min-h-svh`; only a tailwind-merge on that same element REPLACES it.
 * Applying these classes to a new wrapper `<div>`, or to `AppContent` alone,
 * leaves `min-h-svh` alive and reproduces the bug while LOOKING fixed — which
 * is why both shells' specs assert `not.toHaveClass("min-h-svh")` as literals.
 *
 * The unbounded default for the content region stays the literal `"flex-1"` at
 * each call site; it is the absence of this chain, not a member of it.
 */
export const FIXED_VIEWPORT_CHAIN = {
  /** → `<AppShell className>` — replaces `SidebarProvider`'s `min-h-svh`. */
  shell: "min-h-0 flex-1",
  /** → `<AppContent className>` — `SidebarInset` is already `flex-1 flex-col`. */
  content: "min-h-0",
  /** → the content region, so the page's own band can size and scroll. */
  region: "flex min-h-0 flex-1 flex-col",
} as const

/**
 * True when the active Inertia page is a fixed-viewport surface.
 *
 * Branches on the page COMPONENT (stable, server-supplied) rather than on the
 * URL, so a route rename cannot silently drop the chain.
 *
 * @returns whether the calling shell should apply {@link FIXED_VIEWPORT_CHAIN}
 */
export function useIsFixedViewportSurface(): boolean {
  return FIXED_VIEWPORT_COMPONENTS.includes(usePage().component)
}
